import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProviderPageTabs from "../components/ProviderPageTabs";
import { getPaymentsByCompany, getPaymentsByCompanyAllYears, getMultiYearSummary, formatCurrency, checkExclusions, getOwnershipByNPI, getDualInterestByNPI, getProviderInfo, getResearchByNPI, getResearchOwnershipDualInterest, getResearchYearCountByNPI, getResearchGeneralDualInterestByNPI, getOwnershipDetailByNPI, getResearchDetailByNPI, getGeneralPaymentsByNPIAndCompany, getProviderFromNPPES, getPhysicianStatePayments, getDirectResearchByNPI, getPhysicianPodFlags } from "../utils/cmsApi";
import { GeneralVsOwnershipChart, GeneralVsResearchChart } from "../components/DualInterestCharts";
import { useSubscription } from "../context/SubscriptionContext";
import { useMyProviders } from "../context/MyProvidersContext";
import { useUser } from "@clerk/react";
import WatchProviderButton from "../components/WatchProviderButton";
import { getWatchedProvider, watchCompany, unwatchCompany, getWatchedCompanies, queueConfirmation } from "../utils/alertsApi";
import { apiFetch } from "../utils/apiClient";
import SingleProviderScreenPanel from "../components/SingleProviderScreenPanel";
import ProviderScreenButton from "../components/ProviderScreenButton";
import MultiYearChart from "../components/MultiYearChart";
import PaymentDonut from "../components/PaymentDonut";
import PaymentTooltip from "../components/PaymentTooltip";
import CompanyConcentration from "../components/CompanyConcentration";
import CompareButton from "../components/CompareButton";

const CATEGORY_COLORS = {
  "Speaking Fees": "bg-purple-500/10 border-purple-500/20 text-purple-300",
  "Consulting Fees": "bg-blue-500/10 border-blue-500/20 text-blue-300",
  "Research": "bg-teal-500/10 border-teal-500/20 text-teal-300",
  "Food and Beverage": "bg-green-500/10 border-green-500/20 text-green-300",
  "Travel and Lodging": "bg-orange-500/10 border-orange-500/20 text-orange-300",
};

function categoryBadgeClass(cat) {
  return CATEGORY_COLORS[cat] || "bg-white/5 border-white/10 text-white/50";
}

function formatCompact(value) {
  if (!value || value === 0) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function exclusionTypeLabel(code) {
  const types = {
    "1128a1": "Conviction related to Medicare or Medicaid fraud",
    "1128a2": "Conviction relating to patient abuse or neglect",
    "1128a3": "Felony conviction relating to health care fraud",
    "1128a4": "Felony conviction relating to controlled substances",
    "1128b1": "Misdemeanor conviction relating to health care fraud",
    "1128b2": "Conviction relating to obstruction of an investigation",
    "1128b3": "Misdemeanor conviction relating to controlled substances",
    "1128b4": "License revocation or suspension",
    "1128b5": "Exclusion or suspension under federal or state health care program",
    "1128b6": "Claims for excessive charges or unnecessary services",
    "1128b7": "Fraud, kickbacks, and other prohibited activities",
    "1128b8": "Entities controlled by a sanctioned individual",
    "1128b9": "Failure to disclose required information",
    "1128b10": "Failure to supply requested information",
    "1128b11": "Failure to supply payment information",
    "1128b12": "Failure to grant immediate access",
    "1128b13": "Failure to take corrective action",
    "1128b14": "Default on health education loan or scholarship obligations",
    "1128b15": "Individuals controlling a sanctioned entity",
    "1128b16": "Making false statements or misrepresentation",
    "1156": "Failure to meet statutory obligations",
    "1892": "Medicare/Medicaid exclusion",
  };
  return types[code] || code;
}

// ── Collapsible conflict banner ───────────────────────────────────────────────
function CollapsibleBanner({ color, icon, title, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  const colorMap = {
    red:   { border: "border-red-500/30",   bg: "bg-red-500/10",   icon: "text-red-400",   title: "text-red-400",   chevron: "text-red-400/60"   },
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/10", icon: "text-amber-400", title: "text-amber-400", chevron: "text-amber-400/60" },
    teal:  { border: "border-teal-500/30",  bg: "bg-teal-500/10",  icon: "text-teal-400",  title: "text-teal-400",  chevron: "text-teal-400/60"  },
    blue:  { border: "border-blue-500/30",  bg: "bg-blue-500/10",  icon: "text-blue-400",  title: "text-blue-400",  chevron: "text-blue-400/60"  },
  };
  const c = colorMap[color] || colorMap.amber;

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setOpen(!open)}
        className="print:hidden w-full flex items-start gap-2 p-4 text-left"
      >
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
        <span className={`${c.chevron} text-xs mt-0.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
          ▼
        </span>
      </button>
      {/* Print-only always-visible banner header */}
      <div className="hidden print:flex items-start gap-2 p-4">
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      {open && children && (
        <div className="px-4 pb-4 pt-0 border-t border-white/5">
          {children}
        </div>
      )}
      {/* Print: no children — header summary only */}
    </div>
  );
}

function ConflictBanners({
  hasTripleInterest, tripleInterestCompanyIds,
  hasDualInterest, dualInterest,
  researchDualInterest, researchGeneralDualInterest, hasResearchGeneralDual,
  podFlags,
  isPro, setUpgradeMessage, setShowUpgradeModal,
}) {
  return (
    <>
      {/* POD Risk */}
      {podFlags && podFlags.length > 0 && (
        <CollapsibleBanner
          color="blue"
          icon="⚠"
          title={`Potential POD Pattern Detected (${podFlags.length} ${podFlags.length === 1 ? "company" : "companies"})`}
          description={`This provider holds or has held an ownership interest in ${podFlags.length === 1 ? "a company" : `${podFlags.length} companies`} identified as a potential Physician Owned Distributorship (POD) — an arrangement flagged as high-risk in the OIG Work Plan 2026. PODs involve physicians holding equity in companies that distribute medical devices, while also receiving payments from those companies.`}
          defaultOpen={false}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {podFlags.map((p, i) => (
                <div key={i} className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-blue-300 text-xs font-medium">{p.companyName}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full border ${
                          p.podTier === 1
                            ? "bg-blue-500/20 border-blue-500/30 text-blue-300"
                            : "bg-blue-500/10 border-blue-500/20 text-blue-400"
                        }`}>
                          Tier {p.podTier} — {p.podTier === 1 ? "High Confidence" : "Elevated Risk"}
                        </span>
                        {p.hasSameYearOverlap && (
                          <span className="bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">
                            Same-year overlap
                          </span>
                        )}
                        {p.hasDistributorName && (
                          <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                            Distributor name signal
                          </span>
                        )}
                        {p.hasDistributionTerm && (
                          <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                            Distribution term signal
                          </span>
                        )}
                      </div>
                      <p className="text-blue-300/50 text-xs mt-0.5">
                        Ownership reported {p.firstOwnershipYear}
                        {p.lastOwnershipYear !== p.firstOwnershipYear ? `–${p.lastOwnershipYear}` : ""}
                        {" · "}
                        {p.physicianOwnerCount} physician owner{p.physicianOwnerCount !== 1 ? "s" : ""}
                        {" · "}
                        {p.pctOwnersWithPayments.toFixed(0)}% also receive payments
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-blue-400 text-xs font-mono font-medium">
                        {formatCurrency(p.totalOwnershipValue)}
                      </p>
                      <p className="text-blue-300/40 text-xs">ownership value</p>
                      {p.generalPaymentsTotal > 0 && (
                        <>
                          <p className="text-white/40 text-xs font-mono mt-1">
                            {formatCurrency(p.generalPaymentsTotal)}
                          </p>
                          <p className="text-white/30 text-xs">payments from same company</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-blue-300/30 text-xs pt-1">
                POD flags are risk-review indicators, not legal determinations. Source: CMS Open Payments Ownership data · OIG Work Plan 2026. Consult qualified legal and compliance professionals for final determinations.
              </p>
            </div>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to see full POD risk details including company names, ownership values, and payment overlap analysis."); setShowUpgradeModal(true); }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all"
            >
              🔒 Upgrade to Pro to see details
            </button>
          )}
        </CollapsibleBanner>
      )}

      {/* Triple Interest */}
      {hasTripleInterest && (
        <CollapsibleBanner
          color="red"
          icon="⚠"
          title="Triple Interest Conflict"
          description={`This provider has general payments, ownership interests, and research funding from the same ${tripleInterestCompanyIds.size === 1 ? "company" : `${tripleInterestCompanyIds.size} companies`}. This combination carries simultaneous Stark Law, Anti-Kickback Statute, and research integrity exposure.`}
          defaultOpen={false}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest
                .filter((d) => tripleInterestCompanyIds.has(d.companyId))
                .map((d, i) => {
                  const resGen = researchGeneralDualInterest.find((g) => g.companyId === d.companyId);
                  const resDual = researchDualInterest.find((r) => r.companyId === d.companyId);
                  return (
                    <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-red-300 text-xs font-medium">{d.companyName}</p>
                            {d.hasTripleInterestSameYear && <span className="bg-red-500/30 border border-red-500/50 text-red-200 text-xs px-1.5 py-0.5 rounded-full font-medium">⚑ All three same year</span>}
                          </div>
                          <div className="flex gap-2 flex-wrap mt-1">
                            {d.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Payments+Ownership overlap</span>}
                            {resDual?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Ownership overlap</span>}
                            {resGen?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Payments overlap</span>}
                            {resGen?.highPaymentsPreclinicalResearch && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                          </div>
                          <div className="flex gap-4 mt-2 flex-wrap">
                            <div><p className="text-white/40 text-xs font-mono">{formatCurrency(d.generalPaymentsTotal)}</p><p className="text-white/25 text-xs">general payments</p></div>
                            <div><p className="text-amber-400 text-xs font-mono">{formatCurrency(d.valueOfInterest)}</p><p className="text-white/25 text-xs">ownership value</p></div>
                            {resGen && <div><p className="text-teal-400 text-xs font-mono">{formatCurrency(resGen.researchTotal)}</p><p className="text-white/25 text-xs">research funding</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to see full triple interest conflict details."); setShowUpgradeModal(true); }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all"
            >
              🔒 Upgrade to Pro to see details
            </button>
          )}
        </CollapsibleBanner>
      )}

      {/* Ownership + Payments */}
      {hasDualInterest && dualInterest.some((d) => !tripleInterestCompanyIds.has(d.companyId)) && (
        <CollapsibleBanner
          color="amber"
          icon="⚑"
          title="General & Ownership Conflict"
          description={`This provider holds or has held an ownership or investment interest in ${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length === 1 ? "a company" : `${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length} companies`} that also reported general payments to them.`}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).map((d, i) => (
                <div key={i} className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-amber-300 text-xs font-medium">{d.companyName}</p>
                        {d.hasDualInterestSamePeriod && (
                          <span className="bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs px-1.5 py-0.5 rounded-full">Same period</span>
                        )}
                      </div>
                      <p className="text-amber-300/50 text-xs mt-0.5">
                        {d.hasDualInterestSamePeriod
                          ? `Ownership and payments overlap · ${d.ownershipFirstYear}–${d.ownershipLatestYear}`
                          : `Ownership: ${d.ownershipFirstYear}–${d.ownershipLatestYear} · Payments through ${d.generalPaymentsLatestYear}`}
                      </p>
                      <p className="text-amber-300/40 text-xs mt-0.5">
                        {d.interestHolder === "Physician and Immediate Family Member" ? "Held by physician and immediate family member"
                          : d.interestHolder === "Immediate Family Member" ? "Held by immediate family member"
                          : "Held by physician"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-amber-400 text-xs font-mono font-medium">{formatCurrency(d.valueOfInterest)}</p>
                      <p className="text-amber-300/40 text-xs">ownership value</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(d.generalPaymentsTotal)}</p>
                      <p className="text-white/30 text-xs">general payments</p>
                    </div>
                  </div>
                  {d.termsOfInterest && <p className="text-amber-300/30 text-xs mt-2 italic line-clamp-2">{d.termsOfInterest}</p>}
                </div>
              ))}
            </div>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to see full ownership and dual interest details."); setShowUpgradeModal(true); }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 transition-all"
            >
              🔒 Upgrade to Pro to see details
            </button>
          )}
        </CollapsibleBanner>
      )}

      {/* Research + Ownership */}

      {/* Research + General Payments */}
      {hasResearchGeneralDual && researchGeneralDualInterest.some((g) => !tripleInterestCompanyIds.has(g.companyId)) && (
        <CollapsibleBanner
          color="blue"
          icon="⚗"
          title="General & Research Conflict"
          description={(() => {
            const uniqueCount = new Set(researchGeneralDualInterest.filter((g) => !tripleInterestCompanyIds.has(g.companyId)).map((g) => g.companyId)).size;
            const nonTriple = researchGeneralDualInterest.filter((g) => !tripleInterestCompanyIds.has(g.companyId));
            return `This provider received general payments and research funding from the same ${uniqueCount === 1 ? "company" : `${uniqueCount} companies`}.${nonTriple.some((g) => g.hasDualInterestSamePeriod) ? " General payments and research overlap in the same reporting period." : ""}${nonTriple.some((g) => g.highPaymentsPreclinicalResearch) ? " High general payments detected during preclinical research." : ""}`;
          })()}
        >
          {isPro && (
            <div className="space-y-2 mt-3">
              {Object.values(
                researchGeneralDualInterest
                  .filter((g) => !tripleInterestCompanyIds.has(g.companyId))
                  .reduce((acc, g) => {
                    if (!acc[g.companyId]) {
                      acc[g.companyId] = { ...g, paymentTypes: [g.paymentType] };
                    } else {
                      acc[g.companyId].researchTotal += g.researchTotal;
                      acc[g.companyId].paymentTypes.push(g.paymentType);
                      // Keep the most conservative same-period flag (true if either is true)
                      acc[g.companyId].hasDualInterestSamePeriod =
                        acc[g.companyId].hasDualInterestSamePeriod || g.hasDualInterestSamePeriod;
                      acc[g.companyId].highPaymentsPreclinicalResearch =
                        acc[g.companyId].highPaymentsPreclinicalResearch || g.highPaymentsPreclinicalResearch;
                      acc[g.companyId].hasPreclinical =
                        acc[g.companyId].hasPreclinical || g.hasPreclinical;
                    }
                    return acc;
                  }, {})
              ).map((g, i) => (
                <div key={i} className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-blue-300 text-xs font-medium">{g.companyName}</p>
                        {g.paymentTypes?.includes("direct") && (
                          <span className="bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs px-1.5 py-0.5 rounded-full">Direct payment</span>
                        )}
                        {g.hasDualInterestSamePeriod && <span className="bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">Same period</span>}
                        {g.highPaymentsPreclinicalResearch && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                      </div>
                      <p className="text-blue-300/50 text-xs mt-0.5">
                        {g.hasDualInterestSamePeriod
                          ? `Research and payments overlap · ${g.overlapFirstYear}–${g.overlapLatestYear}`
                          : `Research: ${g.researchFirstYear}–${g.researchLatestYear} · Payments: ${g.generalPaymentsFirstYear}–${g.generalPaymentsLatestYear}`}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-teal-400 text-xs font-mono font-medium">{formatCurrency(g.researchTotal)}</p>
                      <p className="text-blue-300/40 text-xs">research funding</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(g.generalPaymentsTotal)}</p>
                      <p className="text-white/30 text-xs">general payments</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isPro && (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to see full research and general payment conflict details."); setShowUpgradeModal(true); }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all"
            >
              🔒 Upgrade to Pro to see details
            </button>
          )}
        </CollapsibleBanner>
      )}
    </>
  );
}

export default function PhysicianPage() {
  const { npi } = useParams();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name") || "This provider";
  const providerState = searchParams.get("state") || "";
  const specialty = searchParams.get("specialty") || "";
  const { isPro, isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const isPaidTier = isPro;
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const { user, isSignedIn } = useUser();
  const { isMyProvider, filterActive, activeList } = useMyProviders();

  // ── Local year state — drives charts only (company list is all-years) ────
  const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
  const availableYears = isPaidTier ? ALL_YEARS : ALL_YEARS.filter((y) => y >= 2023);
  const latestParamYear = parseInt(searchParams.get("latestYear")) || 2025;
  const defaultYear = availableYears.includes(latestParamYear)
    ? latestParamYear
    : availableYears[availableYears.length - 1];
  const [year, setYear] = useState(defaultYear);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [exclusionLoading, setExclusionLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isWatched, setIsWatched] = useState(false);
  const [watchedCompanyNames, setWatchedCompanyNames] = useState(new Map());
  const [companyWatchLoading, setCompanyWatchLoading] = useState(new Set());
  const [screenPanelCompanyId, setScreenPanelCompanyId] = useState(null);
  const [watchedCompanies, setWatchedCompanies] = useState([]);
  const [companyWatchListOpen, setCompanyWatchListOpen] = useState(false);
  const [totalAllYears, setTotalAllYears] = useState(
    parseFloat(searchParams.get("totalAllYears") || "0")
  );

  // ── Resolved provider fields (NPPES fallback when data is incomplete) ────
  const [resolvedState, setResolvedState] = useState(providerState);
  const [resolvedSpecialty, setResolvedSpecialty] = useState(specialty);
  const [resolvedCity, setResolvedCity] = useState("");
  const [resolvedCredential, setResolvedCredential] = useState("");
  const [paymentStates, setPaymentStates] = useState([]);

  useEffect(() => {
    if (!npi || npi === "unknown") return;
    getProviderFromNPPES(npi)
      .then((nppes) => {
        if (!nppes) return;
        if (nppes.state) setResolvedState((prev) => prev || nppes.state);
        if (nppes.specialty) setResolvedSpecialty((prev) => prev || nppes.specialty);
        if (nppes.city) setResolvedCity((prev) => prev || nppes.city);
        if (nppes.credential) setResolvedCredential(nppes.credential);
      })
      .catch(() => {
        if (!providerState) {
          getProviderInfo(npi)
            .then((info) => {
              if (info?.state) setResolvedState(info.state);
            })
            .catch(() => {});
        }
      });
  }, [npi]);

  // ── Ownership state ───────────────────────────────────────────────────────
  const [ownership, setOwnership] = useState([]);
  const [ownershipLoading, setOwnershipLoading] = useState(true);
  const [dualInterest, setDualInterest] = useState([]);
  const [ownershipOpen, setOwnershipOpen] = useState(false);
  const [hasResearch, setHasResearch] = useState(false);
  const [research, setResearch] = useState([]);
  const [researchDualInterest, setResearchDualInterest] = useState([]);
  const [researchGeneralDualInterest, setResearchGeneralDualInterest] = useState([]);
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchYearCount, setResearchYearCount] = useState(0);
  const [ownershipDetail, setOwnershipDetail] = useState([]);
  const [researchDetail, setResearchDetail] = useState([]);
  const [directResearch, setDirectResearch] = useState([]);
  const [podFlags, setPodFlags] = useState([]);
  const [generalPaymentsForCharts, setGeneralPaymentsForCharts] = useState([]);

  // ── Fetch research data ───────────────────────────────────────────────────
  useEffect(() => {
    if (!npi || npi === "unknown") return;
    Promise.all([
      getResearchByNPI(npi),
      getResearchOwnershipDualInterest(npi),
      getResearchYearCountByNPI(npi),
      getResearchGeneralDualInterestByNPI(npi),
    ])
      .then(([researchData, dualData, yearCountData, resGenData]) => {
        const r = researchData || [];
        setResearch(r);
        setHasResearch(r.length > 0);
        setResearchDualInterest(dualData || []);
        setResearchYearCount(yearCountData?.yearCount || 0);
        setResearchGeneralDualInterest(resGenData || []);
      })
      .catch(() => {});

    // Fetch chart data when conflicts exist — runs independently after ownership loads
    Promise.all([
      getOwnershipByNPI(npi),
      getDualInterestByNPI(npi),
    ]).then(([, dualData]) => {
      const dualCompanyIds = (dualData || []).map((d) => d.companyId).filter(Boolean);
      if (dualCompanyIds.length > 0) {
        Promise.all([
          getOwnershipDetailByNPI(npi),
          getGeneralPaymentsByNPIAndCompany(npi, dualCompanyIds),
        ]).then(([od, gp]) => {
          setOwnershipDetail(od || []);
          setGeneralPaymentsForCharts(gp || []);
        }).catch(() => {});
      }
    }).catch(() => {});

    // Fetch research detail + general payments for general vs research chart
    getResearchGeneralDualInterestByNPI(npi).then((resGenData) => {
      if ((resGenData || []).length > 0) {
        const resGenCompanyIds = (resGenData || []).map((g) => g.companyId).filter(Boolean);
        Promise.all([
          getResearchDetailByNPI(npi),
          getGeneralPaymentsByNPIAndCompany(npi, resGenCompanyIds),
          getDirectResearchByNPI(npi),
        ]).then(([rd, gp, dr]) => {
          setResearchDetail(rd || []);
          setDirectResearch(dr || []);
          setGeneralPaymentsForCharts((prev) => {
            // Merge with any existing general payments (from ownership dual interest fetch)
            const existingKeys = new Set(prev.map((p) => `${p.companyId}-${p.year}`));
            const newEntries = (gp || []).filter((p) => !existingKeys.has(`${p.companyId}-${p.year}`));
            return [...prev, ...newEntries];
          });
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [npi]);

  // ── All-years company relationships (replaces year-filtered list) ─────────
  const [allYearsCompanies, setAllYearsCompanies] = useState([]);
  const [allYearsLoading, setAllYearsLoading] = useState(true);
  const [visibleCompanyCount, setVisibleCompanyCount] = useState(10);

  useEffect(() => {
    if (!npi || npi === "unknown") { setAllYearsLoading(false); return; }
    setAllYearsLoading(true);
    getPaymentsByCompanyAllYears(npi)
      .then(setAllYearsCompanies)
      .catch(() => setAllYearsCompanies([]))
      .finally(() => setAllYearsLoading(false));
  }, [npi]);

  // ── Year-filtered data for charts only ────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setCompanies([]);
    getPaymentsByCompany(npi, year)
      .then(setCompanies)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // Fetch state breakdown for selected year
    if (npi && npi !== "unknown") {
      getPhysicianStatePayments(npi, year)
        .then(setPaymentStates)
        .catch(() => setPaymentStates([]));
    }
  }, [npi, year]);

  useEffect(() => {
    setExclusionLoading(true);
    const nameParts = name.split(" ");
    const first = nameParts[0] || "";
    const last = nameParts[nameParts.length - 1] || "";
    checkExclusions(npi, first, last, resolvedState)
      .then((data) => setExclusions({ oig: data.oig || [], sam: data.sam || [] }))
      .catch(() => setExclusions({ oig: [], sam: [] }))
      .finally(() => setExclusionLoading(false));
  }, [npi, name]);

  useEffect(() => {
    if (!npi || npi === "unknown") return;
    getMultiYearSummary(npi)
      .then((data) => {
        const filtered = isPaidTier ? data : data.filter((row) => row.year >= 2023);
        const total = filtered.reduce((sum, row) => sum + row.totalPayments, 0);
        setTotalAllYears(total);
      })
      .catch(() => {});
  }, [npi, isPaidTier]);

  useEffect(() => {
    if (!isSignedIn || !user || !npi || npi === "unknown") return;
    getWatchedProvider(user.id, npi)
      .then((data) => setIsWatched(!!data))
      .catch(() => {});
  }, [isSignedIn, user, npi]);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedCompanies(user.id)
      .then((data) => {
        setWatchedCompanies(data);
        setWatchedCompanyNames(new Map(data.map((c) => [c.company_name, true])));
      })
      .catch(() => {});
  }, [isSignedIn, user]);

  // ── Fetch ownership and dual interest in parallel ─────────────────────────
  useEffect(() => {
    if (!npi || npi === "unknown") {
      setOwnershipLoading(false);
      return;
    }
    setOwnershipLoading(true);
    Promise.all([
      getOwnershipByNPI(npi),
      getDualInterestByNPI(npi),
    ])
      .then(([ownershipData, dualData]) => {
        setOwnership(ownershipData || []);
        setDualInterest(dualData || []);
      })
      .catch(() => {
        setOwnership([]);
        setDualInterest([]);
      })
      .finally(() => setOwnershipLoading(false));

    // Fetch POD flags independently — fires in parallel, doesn't block ownership loading
    getPhysicianPodFlags(npi)
      .then((flags) => setPodFlags(flags || []))
      .catch(() => setPodFlags([]));
  }, [npi]);

  function getCompanyIdByName(name) {
    // Check case-insensitively across all available data sources
    const upper = name.toUpperCase();
    const fromDual = dualInterest.find((d) => d.companyName?.toUpperCase() === upper);
    if (fromDual) return fromDual.companyId;
    const fromOwnership = ownership.find((o) => o.companyName?.toUpperCase() === upper);
    if (fromOwnership) return fromOwnership.companyId;
    const fromResearch = research.find((r) => r.companyName?.toUpperCase() === upper);
    if (fromResearch) return fromResearch.companyId;
    const fromWatched = watchedCompanies.find((c) => c.company_name?.toUpperCase() === upper);
    if (fromWatched?.company_id) return fromWatched.company_id;
    return null;
  }

  async function handleCompanyWatchToggle(e, companyName, companyId = null) {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies and receiving email alerts is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    setCompanyWatchLoading((prev) => new Set(prev).add(companyName));
    try {
      if (watchedCompanyNames.get(companyName)) {
        await unwatchCompany(user.id, companyName);
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, false));
        setWatchedCompanies((prev) => prev.filter((c) => c.company_name !== companyName));
      } else {
        const resolvedId = companyId || getCompanyIdByName(companyName);
        await watchCompany(user.id, companyName, resolvedId);
        await queueConfirmation(user.id, "company", companyName, companyName, { alert_payments: true });
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, true));
        setWatchedCompanies((prev) => [...prev, { company_name: companyName, company_id: resolvedId }]);
      }
    } catch (err) {
      console.error("Company watch toggle error:", err);
    } finally {
      setCompanyWatchLoading((prev) => { const s = new Set(prev); s.delete(companyName); return s; });
    }
  }

  const totalAmount = companies.reduce((s, c) => s + c.total, 0);
  const totalPayments = companies.reduce((s, c) => s + c.count, 0);
  const hasExclusions = exclusions.oig.length > 0 || exclusions.sam.length > 0;
  const hasDualInterest = dualInterest.length > 0;
  const hasOwnership = ownership.length > 0;
  const hasResearchGeneralDual = researchGeneralDualInterest.length > 0;

  // Triple interest: same companyId appears in payments+ownership AND research+ownership AND research+general
  const tripleInterestCompanyIds = new Set(
    dualInterest
      .filter((d) => d.hasTripleInterestAnyYear)
      .map((d) => d.companyId)
  );
  const hasTripleInterest = tripleInterestCompanyIds.size > 0;
  const activeOwnership = ownership.filter((o) => o.hasActiveOwnership);
  const activeResearch = research.filter((r) => r.hasActiveResearch);
  const totalResearchFunding = research.reduce((s, r) => s + r.totalAmount, 0);
  const totalResearchPayments = research.reduce((s, r) => s + r.paymentCount, 0);

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Hide everything not meant for print */
          nav, header, footer,
          .print-hide { display: none !important; }

          /* Force all divs with inline background-color to print */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; }
          div[style*="background-color: rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="background-color: rgba(255,255,255,0.07"] { display: none !important; }

          /* Force page background white */
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }

          /* Cards become plain bordered boxes */
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }

          /* Text color overrides for readability on white */
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }

          /* Banner backgrounds on white */
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          .bg-green-500\\/5 { background: #f0fdf4 !important; border-color: #86efac !important; }

          /* Force conflict banners open */
          .conflict-banner-content { display: block !important; }
          .conflict-banner-chevron { display: none !important; }

          /* SVG / chart text on white */
          svg text { fill: #374151 !important; }

          /* Force all inline-styled bar divs inside chart wrappers to print visibly */
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;

          /* Bar divs inside chart wrappers: force solid color by overriding opacity */
          .print-chart-wrapper div[style*="background-color"] {
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Replace rgba amber (gold bars) with solid amber */
          .print-chart-wrapper div[style*="rgba(251,191,36"] { background-color: #d97706 !important; }
          .print-chart-wrapper div[style*="rgba(139,92,246"] { background-color: #7c3aed !important; }
          .print-chart-wrapper div[style*="rgba(34,197,94"]  { background-color: #16a34a !important; }
          .print-chart-wrapper div[style*="rgba(59,130,246"] { background-color: #2563eb !important; }
          .print-chart-wrapper div[style*="rgba(239,68,68"]  { background-color: #dc2626 !important; }
          .print-chart-wrapper div[style*="rgba(249,115,22"] { background-color: #ea580c !important; }
          .print-chart-wrapper div[style*="rgba(20,184,166"] { background-color: #0f766e !important; }
          .print-chart-wrapper div[style*="rgba(236,72,153"] { background-color: #db2777 !important; }
          /* Hide empty bar placeholders */
          .print-chart-wrapper div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          .print-chart-wrapper div[style*="rgba(255,255,255,0.07"] { display: none !important; }

          /* Hide dropdown selects and interactive toggles from charts */
          select { display: none !important; }

          /* Chart controls hidden at source via print:hidden in components */
        }
          
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">ProviderPayWatch · Provider Profile Report</p>
            <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              NPI: {npi}
              {resolvedCredential ? ` · ${resolvedCredential}` : ""}
              {resolvedSpecialty && resolvedSpecialty !== "Individual" ? ` · ${resolvedSpecialty}` : ""}
              {[resolvedCity, resolvedState].filter(Boolean).length > 0 ? ` · ${[resolvedCity, resolvedState].filter(Boolean).join(", ")}` : ""}
            </p>
            {paymentStates.length > 0 && resolvedState && (() => {
              const outOfState = paymentStates.filter((s) => s.state !== resolvedState);
              if (outOfState.length === 0) return null;
              return (
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>{year} payments in:</span>
                  {paymentStates.map((s) => {
                    const isHome = s.state === resolvedState;
                    const amt = s.totalPayments >= 1_000_000
                      ? `$${(s.totalPayments / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
                      : s.totalPayments >= 1_000
                      ? `$${(s.totalPayments / 1_000).toFixed(0)}K`
                      : `$${s.totalPayments.toFixed(0)}`;
                    return (
                      <span key={s.state} style={{
                        fontSize: "11px", padding: "2px 8px", borderRadius: "9999px",
                        border: `1px solid ${isHome ? "#d1d5db" : "#f59e0b"}`,
                        color: isHome ? "#9ca3af" : "#b45309",
                        background: isHome ? "#f9fafb" : "#fffbeb",
                      }}>
                        {s.state} {amt}
                      </span>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
      </div>

      <Link to={-1} className="print:hidden text-white/40 hover:text-white/70 text-sm transition-colors">
        ← Results
      </Link>

      <div className="mt-4 mb-8">
        <h1 className="print:hidden font-display text-4xl text-white leading-tight">{name}</h1>
        <div className="print:hidden flex items-center gap-3 mt-1 flex-wrap">
          {npi && npi !== "undefined" && npi !== "null" && npi !== "unknown" ? (
            <p className="text-white/30 text-xs font-mono">NPI: {npi}</p>
          ) : (
            <p className="text-yellow-400/60 text-xs">⚠ No NPI on file for this provider</p>
          )}
          {isMyProvider(npi) && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/30 text-gold-400">
              ◉ {activeList?.list_name || "My Providers"}
            </span>
          )}
          {(resolvedSpecialty || resolvedCity || resolvedState || resolvedCredential) && (
            <p className="text-white/30 text-xs">
              {[
                resolvedCredential,
                resolvedSpecialty && resolvedSpecialty !== "Individual" ? resolvedSpecialty : null,
                [resolvedCity, resolvedState].filter(Boolean).join(", "),
              ].filter(Boolean).join(" · ")}
            </p>
          )}
          {/* Out-of-state payment badges — shown when provider received payments
              in states other than their NPPES practice location */}
          {paymentStates.length > 0 && resolvedState && (() => {
            const outOfState = paymentStates.filter((s) => s.state !== resolvedState);
            if (outOfState.length === 0) return null;
            return (
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                <span className="text-white/20 text-xs shrink-0">{year} payments in:</span>
                {paymentStates.map((s) => {
                  const isHome = s.state === resolvedState;
                  return (
                    <span
                      key={s.state}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        isHome
                          ? "bg-white/5 border-white/10 text-white/30"
                          : "bg-amber-500/10 border-amber-500/25 text-amber-400/80"
                      }`}
                    >
                      {s.state} {formatCompact(s.totalPayments)}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied ? <><span>✓</span><span>Link copied!</span></> : <><span>⎘</span><span>Share profile</span></>}
          </button>
          <div className="print:hidden">
            <WatchProviderButton
              npi={npi}
              providerName={name}
              initialWatched={isWatched}
              onWatchChange={(npi, watched) => setIsWatched(watched)}
            />
          </div>
          <div className="print:hidden">
            <CompareButton provider={{
            npi,
            firstName: name.split(" ")[0] || "",
            lastName: name.split(" ").slice(1).join(" ") || "",
            state: resolvedState,
            totalPayments: totalAllYears,
          }} />
          </div>
          {isPro ? (
            <button
              onClick={() => window.print()}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
            >
              <span>⎙</span><span>Print / Save PDF</span>
            </button>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to print and save provider profiles as PDF."); setShowUpgradeModal(true); }}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/40 bg-white/5 transition-all"
            >
              <span>🔒</span><span>Print / Save PDF</span>
            </button>
          )}
          <ProviderScreenButton
            npi={npi}
            providerName={name}
            userId={user?.id}
            fetchFn={apiFetch}
            isEnterprise={isEnterprise}
            onLockedClick={() => { setUpgradeMessage("Screening a provider for conflicts is an Enterprise feature."); setShowUpgradeModal(true); }}
          />
        </div>

        {/* ── Exclusion Banners ─────────────────────────────────────────── */}
        {!exclusionLoading && hasExclusions && (
          <div className="mt-4 space-y-2">
            {exclusions.oig.map((e, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-red-400 font-medium text-sm">OIG Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-red-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-red-300/50 text-xs mt-0.5">{exclusionTypeLabel(e.exclusionType)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-red-500/20">
                  {e.reinstatedDate && <p className="text-green-400 text-xs">Reinstated: {e.reinstatedDate}</p>}
                  <a href="https://exclusions.oig.hhs.gov" target="_blank" rel="noopener noreferrer"
                    className="text-red-400/50 hover:text-red-400 text-xs transition-colors ml-auto">
                    Verify on OIG website →
                  </a>
                </div>
              </div>
            ))}
            {exclusions.sam.map((e, i) => (
              <div key={i} className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-orange-400 font-medium text-sm">SAM.gov Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-orange-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-orange-300/50 text-xs mt-0.5">{e.exclusionType}</p>
                    {e.excludingAgency && <p className="text-orange-300/40 text-xs mt-0.5">{e.excludingAgency}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-orange-500/20">
                  {e.terminationDate && e.terminationDate !== "Indefinite" && (
                    <p className="text-orange-300/50 text-xs">Terminates: {e.terminationDate}</p>
                  )}
                  <a href="https://sam.gov/search/?index=ei&page=1&pageSize=25&sort=-score&sfm%5Bstatus%5D%5B0%5D=Active"
                    target="_blank" rel="noopener noreferrer"
                    className="text-orange-400/50 hover:text-orange-400 text-xs transition-colors ml-auto">
                    Verify on SAM.gov →
                  </a>
                </div>
              </div>
            ))}
            <p className="text-white/20 text-xs">
              Sources: HHS OIG LEIE (updated monthly) · SAM.gov (updated daily) · Some records may contain data from Dun &amp; Bradstreet.
            </p>
          </div>
        )}

        {/* ── Conflict Banners ──────────────────────────────────────────── */}
        {!ownershipLoading && (hasTripleInterest || hasDualInterest || hasResearchGeneralDual || podFlags.length > 0) && (
          <div className="mt-4 space-y-3">
            {/* On print: if triple interest exists, show only that banner */}
            {hasTripleInterest && (
              <div className="hidden print:block">
                <ConflictBanners
                  hasTripleInterest={true}
                  tripleInterestCompanyIds={tripleInterestCompanyIds}
                  hasDualInterest={false}
                  dualInterest={[]}
                  researchDualInterest={[]}
                  researchGeneralDualInterest={[]}
                  hasResearchGeneralDual={false}
                  podFlags={[]}
                  isPro={isPro}
                  setUpgradeMessage={setUpgradeMessage}
                  setShowUpgradeModal={setShowUpgradeModal}
                />
              </div>
            )}
            {/* Screen: show all banners. Print: hide all if triple interest (replaced above) */}
            <div className={hasTripleInterest ? "print:hidden" : ""}>
              <ConflictBanners
                hasTripleInterest={hasTripleInterest}
                tripleInterestCompanyIds={tripleInterestCompanyIds}
                hasDualInterest={hasDualInterest}
                dualInterest={dualInterest}
                researchDualInterest={researchDualInterest}
                researchGeneralDualInterest={researchGeneralDualInterest}
                hasResearchGeneralDual={hasResearchGeneralDual}
                podFlags={podFlags}
                isPro={isPro}
                setUpgradeMessage={setUpgradeMessage}
                setShowUpgradeModal={setShowUpgradeModal}
              />
            </div>
          </div>
        )}

        {/* ── No Data Confirmation ──────────────────────────────────────── */}
        {!loading && !exclusionLoading && !ownershipLoading && !allYearsLoading && allYearsCompanies.length === 0 && !hasExclusions && !hasOwnership && !hasResearch && (
          <div className="mt-4 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <span className="text-green-400 text-sm mt-0.5">✓</span>
              <div>
                <p className="text-green-400 font-medium text-sm">No Open Payments records found</p>
                <p className="text-white/40 text-xs mt-1">
                  No general payments, research payments, or ownership interests were reported to CMS for this provider between 2016–2025. This provider is not on the OIG or SAM.gov exclusion lists.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── No Payments but has exclusions/ownership/research ─────────── */}
        {!loading && !exclusionLoading && !ownershipLoading && !allYearsLoading && allYearsCompanies.length === 0 && (hasExclusions || hasOwnership || hasResearch) && (
          <div className="mt-4 bg-white/[0.03] border border-white/10 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <span className="text-white/40 text-sm mt-0.5">◎</span>
              <div>
                <p className="text-white/50 font-medium text-sm">No Open Payments records found</p>
                <p className="text-white/30 text-xs mt-1">
                  No general payments were reported to CMS for this provider between 2016–2025.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Page tab bar ──────────────────────────────────────────────── */}
        {!ownershipLoading && (
          <div className="print:hidden mt-4">
            <ProviderPageTabs
              npi={npi}
              name={name}
              state={resolvedState}
              specialty={resolvedSpecialty || specialty}
              totalPayments={totalAllYears}
              ownershipCount={ownership.length}
              activeOwnershipCount={activeOwnership.length}
              researchCount={research.length}
              researchPayments={totalResearchPayments}
              researchYears={researchYearCount}
              activePage="general"
            />
          </div>
        )}

        {/* ── Stats Row ─────────────────────────────────────────────────── */}
        {!loading && !allYearsLoading && companies.length > 0 && (
          <div className="flex gap-4 mt-4 flex-wrap">
            <div className="card py-3 px-5 text-center">
              <div className="text-gold-400 font-display text-2xl">{formatCurrency(totalAllYears)}</div>
              <div className="text-white/30 text-xs mt-0.5">
                {isPaidTier ? "total all years" : "total 2023–present"}
              </div>
            </div>
            <div className="card py-3 px-5 text-center">
              <div className="text-white font-display text-2xl">{formatCurrency(totalAmount)}</div>
              <div className="text-white/30 text-xs mt-0.5">total received ({year})</div>
            </div>
            <div className="card py-3 px-5 text-center">
              <div className="text-white font-display text-2xl">{totalPayments}</div>
              <div className="text-white/30 text-xs mt-0.5">payments</div>
            </div>
            <div className="card py-3 px-5 text-center">
              <div className="text-white font-display text-2xl">{companies.length}</div>
              <div className="text-white/30 text-xs mt-0.5">companies</div>
            </div>

          </div>
        )}
      </div>      

      {/* ── Year selector — drives charts below ───────────────────────────── */}
      <div className="print:hidden flex items-center justify-between mb-4">
        <p className="text-white/25 text-xs uppercase tracking-widest">Payment history</p>
        <div className="flex items-center gap-2">
          {!isPaidTier && (
            <span className="text-white/25 text-xs">2023–present ·</span>
          )}
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gold-500/60"
          >
            {availableYears.slice().reverse().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>
      {!isPaidTier && (
        <p className="print:hidden text-white/20 text-xs mb-3 text-right -mt-2">
          <button onClick={() => { setUpgradeMessage("Upgrade to Pro for full payment history back to 2016."); setShowUpgradeModal(true); }} className="text-gold-400 hover:text-gold-300 transition-colors">
            Upgrade for full history
          </button>
        </p>
      )}

      <div className="print-chart-wrapper">
        <MultiYearChart npi={npi} year={year} setYear={setYear} isPaidTier={isPaidTier} providerName={name} />
      </div>
      <div className="print-chart-wrapper">
        <PaymentDonut npi={npi} year={year} />
      </div>
      <div className="print-chart-wrapper">
        <CompanyConcentration companies={companies} npi={npi} name={name} year={year} />
      </div>

      {/* Dual interest charts — screen only */}
      <div className="print:hidden">
      {isPro && hasDualInterest && ownershipDetail.length > 0 && generalPaymentsForCharts.length > 0 && (
        <GeneralVsOwnershipChart
          conflicts={dualInterest}
          generalPayments={generalPaymentsForCharts}
          ownershipDetail={ownershipDetail}
          providerName={name}
          npi={npi}
        />
      )}
      {isPro && hasResearchGeneralDual && generalPaymentsForCharts.length > 0 && (
        <GeneralVsResearchChart
          conflicts={researchGeneralDualInterest}
          generalPayments={generalPaymentsForCharts}
          researchDetail={researchDetail}
          directResearch={directResearch}
          providerName={name}
          npi={npi}
        />
      )}
      
      </div>{/* end print:hidden dual interest charts */}

      {/* ── Ownership Section — screen only ──────────────────────────────── */}
      {!ownershipLoading && hasOwnership && (
        <div className="print:hidden">
          <div className="card mt-6">
          <button
            onClick={() => {
              if (!isPro) {
                setUpgradeMessage(
                  "Upgrade to Pro to view full ownership and investment interest records including company names, interest values, and year-by-year history."
                );
                setShowUpgradeModal(true);
                return;
              }
              setOwnershipOpen(!ownershipOpen);
            }}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-amber-400/80 text-sm">🏦</span>
              <span className="text-white/60 text-sm">Ownership &amp; Investment Interests</span>
              <span className="bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs px-2 py-0.5 rounded-full">
                {ownership.length} {ownership.length === 1 ? "company" : "companies"}
              </span>
              {activeOwnership.length > 0 && (
                <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-2 py-0.5 rounded-full">
                  {activeOwnership.length} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isPro && (
                <Link
                  to={`/physician/${npi}/ownership?name=${encodeURIComponent(name)}&state=${encodeURIComponent(providerState)}&specialty=${encodeURIComponent(ownership[0]?.specialty || "")}&totalPayments=${totalAllYears || 0}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-amber-400/60 hover:text-amber-400 text-xs transition-colors"
                >
                  Full report →
                </Link>
              )}
              {isPro ? (
                <span className="text-white/30 text-xs">{ownershipOpen ? "▲" : "▼"}</span>
              ) : (
                <span className="text-amber-400/60 text-xs">🔒 Pro</span>
              )}
            </div>
          </button>          

          {isPro && ownershipOpen && (
            <div className="mt-4 space-y-3">
              {ownership.map((o, i) => (
                <div
                  key={i}
                  className={`rounded-lg border px-4 py-3 ${
                    o.hasActiveOwnership
                      ? "bg-amber-500/5 border-amber-500/20"
                      : "bg-white/[0.03] border-white/[0.08]"
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white/80 text-sm font-medium truncate">{o.companyName}</p>
                        {o.hasActiveOwnership && (
                          <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                            Active 2025
                          </span>
                        )}
                      </div>
                      <p className="text-white/30 text-xs mt-0.5">
                        {o.firstYear === o.latestYear
                          ? `Reported ${o.firstYear}`
                          : `${o.firstYear}–${o.latestYear} · ${o.yearCount} year${o.yearCount !== 1 ? "s" : ""}`}
                      </p>
                      <p className="text-white/40 text-xs mt-1">
                        {o.interestHolder === "Physician and Immediate Family Member"
                          ? "Physician and immediate family member"
                          : o.interestHolder === "Immediate Family Member"
                          ? "Immediate family member"
                          : "Physician"}
                      </p>
                      {o.termsOfInterest && (
                        <p className="text-white/25 text-xs mt-1 italic line-clamp-2">{o.termsOfInterest}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {o.valueOfInterest > 0 && (
                        <>
                          <p className="text-amber-400 text-sm font-mono font-medium">
                            {formatCurrency(o.valueOfInterest)}
                          </p>
                          <p className="text-white/30 text-xs">current interest value</p>
                        </>
                      )}
                      {o.totalAmountInvested > 0 && (
                        <>
                          <p className="text-white/50 text-xs font-mono mt-1">
                            {formatCurrency(o.totalAmountInvested)}
                          </p>
                          <p className="text-white/25 text-xs">amount invested</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-white/20 text-xs pt-1">
                Source: CMS Open Payments Ownership &amp; Investment Interest data · Reported by applicable manufacturers and GPOs
              </p>
            </div>
          )}
        </div>
          {isPro && (
            <div className="mt-2 flex justify-end">
              <Link
                to={`/physician/${npi}/ownership?name=${encodeURIComponent(name)}&state=${encodeURIComponent(providerState)}&specialty=${encodeURIComponent(ownership[0]?.specialty || "")}&totalPayments=${totalAllYears || 0}`}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:border-amber-500/50 transition-all"
              >
                <span>🏦</span>
                <span>View full ownership report</span>
                <span>→</span>
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ── Research Section — screen only ───────────────────────────────── */}
      {hasResearch && (
        <div className="print:hidden">
          <div className="card mt-6">
          <button
            onClick={() => {
              if (!isPro) {
                setUpgradeMessage(
                  "Upgrade to Pro to view full research payment records including company names, funding amounts, and study details."
                );
                setShowUpgradeModal(true);
                return;
              }
              setResearchOpen(!researchOpen);
            }}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-teal-400/80 text-sm">🔬</span>
              <span className="text-white/60 text-sm">Research Payments</span>
              <span className="bg-teal-500/20 border border-teal-500/30 text-teal-400 text-xs px-2 py-0.5 rounded-full">
                {research.length} {research.length === 1 ? "company" : "companies"}
              </span>
              {activeResearch.length > 0 && (
                <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-2 py-0.5 rounded-full">
                  {activeResearch.length} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isPro && (
                <Link
                  to={`/physician/${npi}/research?name=${encodeURIComponent(name)}&state=${encodeURIComponent(resolvedState)}&specialty=${encodeURIComponent(specialty || "")}&totalPayments=${totalAllYears || 0}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-teal-400/60 hover:text-teal-400 text-xs transition-colors"
                >
                  Full report →
                </Link>
              )}
              {isPro ? (
                <span className="text-white/30 text-xs">{researchOpen ? "▲" : "▼"}</span>
              ) : (
                <span className="text-teal-400/60 text-xs">🔒 Pro</span>
              )}
            </div>
          </button>

          {isPro && researchOpen && (
            <div className="mt-4 space-y-3">
              {research.map((r, i) => {
                const isDual = researchDualInterest.some((d) => d.companyId === r.companyId);
                return (
                  <div
                    key={i}
                    className={`rounded-lg border px-4 py-3 ${
                      r.hasActiveResearch
                        ? "bg-teal-500/5 border-teal-500/20"
                        : "bg-white/[0.03] border-white/[0.08]"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white/80 text-sm font-medium truncate">{r.companyName}</p>
                          {r.hasActiveResearch && (
                            <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                              Active 2025
                            </span>
                          )}
                          {researchDualInterest.some((d) => d.companyId === r.companyId) && tripleInterestCompanyIds.has(r.companyId) ? (
                            <span className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                              ⚠ Triple Interest
                            </span>
                          ) : researchDualInterest.some((d) => d.companyId === r.companyId) && (
                            <span className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                              ⚑ Dual Interest
                            </span>
                          )}
                        </div>
                        <p className="text-white/30 text-xs mt-0.5">
                          {r.firstYear === r.latestYear
                            ? `Reported ${r.firstYear}`
                            : `${r.firstYear}–${r.latestYear}`}
                          {" · "}{r.paymentCount} {r.paymentCount === 1 ? "payment" : "payments"}
                        </p>
                        {r.primaryContext && (
                          <p className="text-white/40 text-xs mt-1">{r.primaryContext}</p>
                        )}
                        {r.primaryProduct && (
                          <p className="text-white/25 text-xs mt-0.5 italic">{r.primaryProduct}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-teal-400 text-sm font-mono font-medium">
                          {formatCurrency(r.totalAmount)}
                        </p>
                        <p className="text-white/30 text-xs">research funding</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="text-white/20 text-xs pt-1">
                Source: CMS Open Payments Research · Reported by applicable manufacturers and GPOs
              </p>
            </div>
          )}
        </div>
          <div className="mt-2 flex justify-end">
            <Link
              to={`/physician/${npi}/research?name=${encodeURIComponent(name)}&state=${encodeURIComponent(resolvedState)}&specialty=${encodeURIComponent(specialty || "")}&totalPayments=${totalAllYears || 0}`}
              className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-teal-500/30 bg-teal-500/10 text-teal-400 hover:bg-teal-500/20 hover:border-teal-500/50 transition-all"
            >
              <span>🔬</span>
              <span>View full research report</span>
              <span>→</span>
            </Link>
          </div>
        </div>
      )}

      {/* ── Company Relationships — all years ────────────────────────────── */}
      <div className="print:hidden flex items-center justify-between mb-3 mt-6">
        <h2 className="text-white/40 text-xs uppercase tracking-widest">Company relationships</h2>
        <span className="text-white/25 text-xs">2016–2025</span>
      </div>

      {allYearsLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-2/3 mb-3" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          ))}
        </div>
      )}

      {!allYearsLoading && !loading && allYearsCompanies.length === 0 && (
        <div className="card text-center py-12">
          {!npi || npi === "undefined" || npi === "null" || npi === "unknown" ? (
            <>
              <p className="text-yellow-400/70 text-sm mb-2">⚠ This provider is missing an NPI</p>
              <p className="text-white/30 text-xs">Payment records cannot be retrieved without a valid NPI number.</p>
            </>
          ) : (
            <p className="text-white/50 text-sm">No payment records found for this provider.</p>
          )}
        </div>
      )}

      {/* ── Print-only: Company Relationships top 5 ───────────────────────── */}
      {!allYearsLoading && allYearsCompanies.length > 0 && (
        <div className="hidden print:block mt-6 mb-4">
          <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">Company Relationships (Top 5) · 2016–2025</h2>
          <div className="space-y-2">
            {allYearsCompanies.slice(0, 5).map((company) => {
              const isTriple = tripleInterestCompanyIds.has(company.companyId);
              const isDual = !isTriple && (
                dualInterest.some((d) => d.companyId === company.companyId) ||
                researchDualInterest.some((r) => r.companyId === company.companyId) ||
                researchGeneralDualInterest.some((g) => g.companyId === company.companyId)
              );
              const yearRange = company.firstYear && company.latestYear
                ? company.firstYear === company.latestYear ? `${company.firstYear}` : `${company.firstYear}–${company.latestYear}`
                : null;
              return (
                <div key={company.companyId || company.companyName}
                  className="border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-900 text-sm font-medium">{company.companyName}</p>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                        {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                        {yearRange && <span className="text-xs text-gray-400">{yearRange}</span>}
                        {company.yearCount > 1 && <span className="text-xs text-gray-400">{company.yearCount} years</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(company.totalPayments)}</p>
                      <p className="text-gray-400 text-xs">{company.paymentCount.toLocaleString()} payment{company.paymentCount !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {allYearsCompanies.length > 5 && (
            <p className="text-gray-400 text-xs mt-2">Showing 5 of {allYearsCompanies.length} companies · export full list from ProviderPayWatch</p>
          )}
        </div>
      )}

      {/* Top controls — count toggle + export */}
      {!allYearsLoading && allYearsCompanies.length > 0 && isPro && (
        <div className="print:hidden flex items-center justify-between mb-3 flex-wrap gap-2">
          {allYearsCompanies.length > 10 ? (
            <button
              onClick={() => setVisibleCompanyCount((prev) => prev === 10 ? 50 : 10)}
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
            >
              <span>{visibleCompanyCount === 10 ? "▼" : "▲"}</span>
              <span>
                {visibleCompanyCount === 10
                  ? `Show top 50 of ${allYearsCompanies.length} companies`
                  : `Show top 10 of ${allYearsCompanies.length} companies`}
              </span>
            </button>
          ) : (
            <span className="text-white/25 text-xs">{allYearsCompanies.length} {allYearsCompanies.length === 1 ? "company" : "companies"}</span>
          )}
          <button
            onClick={() => exportCsv(allYearsCompanies, name, npi, tripleInterestCompanyIds, dualInterest, researchDualInterest, researchGeneralDualInterest, EXPORT_LIMIT)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
          >
            ⬇ Export all to CSV
          </button>
        </div>
      )}

      <div className="print:hidden space-y-3">
        {(() => {
          const displayList = isPro
            ? allYearsCompanies.slice(0, visibleCompanyCount)
            : allYearsCompanies.slice(0, 5);
          return displayList.map((company) => {
            const isTriple = tripleInterestCompanyIds.has(company.companyId);
            const isDual = !isTriple && (
              dualInterest.some((d) => d.companyId === company.companyId) ||
              researchDualInterest.some((r) => r.companyId === company.companyId) ||
              researchGeneralDualInterest.some((g) => g.companyId === company.companyId)
            );
            const yearRange = company.firstYear && company.latestYear
              ? company.firstYear === company.latestYear
                ? `${company.firstYear}`
                : `${company.firstYear}–${company.latestYear}`
              : null;
            return (
              <Link
                key={company.companyId || company.companyName}
                to={`/physician/${npi}/company/${encodeURIComponent(company.companyName)}?name=${encodeURIComponent(name)}`}
                className={`card block hover:border-gold-500/30 hover:bg-navy-700/50 transition-all group ${
                  isTriple ? "border-red-500/20 bg-red-500/[0.02]" : isDual ? "border-orange-500/20 bg-orange-500/[0.02]" : ""
                }`}
              >
                <div className="flex justify-between items-start">
                  <h2 className="text-white font-medium group-hover:text-gold-300 transition-colors pr-4">
                    {company.companyName}
                  </h2>
                  <div className="text-right shrink-0">
                    <div className="text-gold-400 font-medium font-mono text-sm">{formatCurrency(company.totalPayments)}</div>
                    <div className="text-white/30 text-xs">{company.paymentCount.toLocaleString()} payment{company.paymentCount !== 1 ? "s" : ""}</div>
                    {company.paymentCount > 0 && (
                      <div className="text-white/20 text-xs mt-0.5">avg {formatCurrency(company.totalPayments / company.paymentCount)}</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {isTriple && (
                    <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                      ⚠ Triple Interest
                    </span>
                  )}
                  {isDual && (
                    <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs">
                      ⚑ Dual Interest
                    </span>
                  )}
                  {yearRange && (
                    <span className="badge bg-white/5 border border-white/10 text-white/40 text-xs">
                      {yearRange}
                    </span>
                  )}
                  {company.yearCount > 1 && (
                    <span className="badge bg-white/5 border border-white/10 text-white/30 text-xs">
                      {company.yearCount} years
                    </span>
                  )}
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  {isEnterprise ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setScreenPanelCompanyId(screenPanelCompanyId === company.companyId ? null : company.companyId);
                      }}
                      className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all"
                    >
                      🛡 Screen
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUpgradeMessage("Screening a company for conflicts against this provider is an Enterprise feature.");
                        setShowUpgradeModal(true);
                      }}
                      className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
                    >
                      🔒 Screen <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
                    </button>
                  )}
                  <button
                    onClick={(e) => handleCompanyWatchToggle(e, company.companyName, company.companyId)}
                    disabled={companyWatchLoading.has(company.companyName)}
                    className={`text-xs px-3 py-1 rounded-lg border transition-all ${
                      watchedCompanyNames.get(company.companyName)
                        ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
                        : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
                    }`}
                  >
                    {companyWatchLoading.has(company.companyName)
                      ? "..."
                      : watchedCompanyNames.get(company.companyName)
                      ? "🔔 Watching"
                      : "🔕 Watch"}
                  </button>
                </div>
                {screenPanelCompanyId === company.companyId && (
                  <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <SingleProviderScreenPanel
                      companyId={company.companyId}
                      companyName={company.companyName}
                      npi={npi}
                      providerName={name}
                      userId={user?.id}
                      fetchFn={apiFetch}
                      onCancel={() => setScreenPanelCompanyId(null)}
                    />
                  </div>
                )}
              </Link>
            );
          });
        })()}
      </div>

      {/* Show more / less toggle + bottom export — Pro only, screen only */}
      {isPro && allYearsCompanies.length > 10 && (
        <div className="print:hidden">
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <button
              onClick={() => setVisibleCompanyCount((prev) => prev === 10 ? 50 : 10)}
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
            >
              <span>{visibleCompanyCount === 10 ? "▼" : "▲"}</span>
              <span>
                {visibleCompanyCount === 10
                  ? `Show top 50 of ${allYearsCompanies.length} companies`
                  : `Show top 10 of ${allYearsCompanies.length} companies`}
              </span>
            </button>
            <button
              onClick={() => exportCsv(allYearsCompanies, name, npi, tripleInterestCompanyIds, dualInterest, researchDualInterest, researchGeneralDualInterest)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
            >
              ⬇ Export all to CSV
            </button>
          </div>
        </div>
      )}

      {/* Free tier gate — screen only */}
      {!isPro && allYearsCompanies.length > 5 && (
        <div className="print:hidden">
          <div className="card border-gold-500/20 bg-gold-500/5 text-center py-6 mt-3">
            <p className="text-gold-400 font-medium text-sm mb-1">
              🔒 {allYearsCompanies.length - 5} more {allYearsCompanies.length - 5 === 1 ? "company" : "companies"} hidden
            </p>
            <p className="text-white/40 text-xs mb-3">Upgrade to Pro to see all company relationships</p>
            <button
              onClick={() => {
                setUpgradeMessage(`You're seeing 5 of this provider's company relationships. Upgrade to Pro to see all ${allYearsCompanies.length}.`);
                setShowUpgradeModal(true);
              }}
              className="btn-primary text-xs px-4 py-2"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      )}

      {/* ── Company Watch List — screen only ─────────────────────────────── */}
      {isSignedIn && isPro && companies.length > 0 && (
        <div className="print:hidden">
          <div className="card mt-4">
          <button
            onClick={() => setCompanyWatchListOpen(!companyWatchListOpen)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm">🏢 Company Watch List</span>
              {watchedCompanies.length > 0 && (
                <span className="bg-gold-500/20 border border-gold-500/30 text-gold-400 text-xs px-2 py-0.5 rounded-full">
                  {watchedCompanies.length}
                </span>
              )}
            </div>
            <span className="text-white/30 text-xs">{companyWatchListOpen ? "▲" : "▼"}</span>
          </button>
          {companyWatchListOpen && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
              {watchedCompanies.length === 0 ? (
                <p className="text-white/30 text-sm text-center py-4">
                  You aren't watching any companies yet. Click "🔕 Watch" on any company card to add them.
                </p>
              ) : (
                watchedCompanies.map((c) => (
                  <div key={c.company_name} className="flex items-center justify-between bg-navy-700/50 border border-white/5 rounded-lg px-3 py-2">
                    <Link
                      to={c.company_id
                        ? `/company/${encodeURIComponent(c.company_id)}`
                        : `/company-results?q=${encodeURIComponent(c.company_name)}`}
                      className="text-white/80 text-sm hover:text-gold-300 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.company_name}
                    </Link>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await unwatchCompany(user.id, c.company_name);
                        setWatchedCompanies((prev) => prev.filter((w) => w.company_name !== c.company_name));
                        setWatchedCompanyNames((prev) => new Map(prev).set(c.company_name, false));
                      }}
                      className="text-white/20 hover:text-red-400 text-xs transition-colors shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        </div>
      )}

      {!allYearsLoading && allYearsCompanies.length > 0 && !isPro && (
        <div className="print:hidden mt-6 text-center">
          <p className="text-white/20 text-xs">Pro subscribers can export results to CSV.</p>
        </div>
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments · Payments disclosed by manufacturers as required by the Sunshine Act
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8 border-t border-gray-200 pt-4">
        Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}

function exportCsv(companies, name, npi, tripleInterestCompanyIds, dualInterest, researchDualInterest, researchGeneralDualInterest, exportLimit = 500) {
  const rows = [["Provider Name", "NPI", "Company", "Total ($)", "Payments", "First Year", "Latest Year", "Years Active", "Interest Flag"]];
  const exportData = exportLimit === Infinity ? companies : companies.slice(0, exportLimit);
  for (const c of exportData) {
    const isTriple = tripleInterestCompanyIds.has(c.companyId);
    const isDual = !isTriple && (
      dualInterest.some((d) => d.companyId === c.companyId) ||
      researchDualInterest.some((r) => r.companyId === c.companyId) ||
      researchGeneralDualInterest.some((g) => g.companyId === c.companyId)
    );
    const flag = isTriple ? "Triple Interest" : isDual ? "Dual Interest" : "";
    rows.push([
      name,
      npi,
      c.companyName,
      (c.totalPayments || 0).toFixed(2),
      c.paymentCount,
      c.firstYear || "",
      c.latestYear || "",
      c.yearCount || "",
      flag,
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/\s+/g, "_")}_payments.csv`;
  a.click();
}